import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  Badge,
  Button,
  Card,
  PageHeader,
  Skeleton,
  formatDateTimeVn,
  formatVnd,
} from "@chotca/ui";
import {
  qk,
  usePlanPurchaseDetail,
  usePlanPurchaseQr,
} from "@chotca/api-client";
import { useAuthStore } from "@chotca/auth";
import type {
  OrgPlanPurchaseDetail,
  PlanPurchaseQrResponse,
} from "@chotca/config";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";
import { VietQrDialog } from "@/components/employer/vietqr-dialog";

type Detail = OrgPlanPurchaseDetail;

const STATUS_TONE: Record<
  Detail["status"],
  "lime" | "sun" | "smoke" | "ember"
> = {
  PAID: "lime",
  PENDING: "sun",
  EXPIRED: "smoke",
  FAILED: "ember",
};

// Invoice / payment-result screen for a single order (/billing/:orderCode).
// Serves double duty: the landing screen right after "Đã chuyển khoản" (still
// PENDING → self-updates to PAID via the polling hook) and a re-openable invoice
// from the billing history. Direct-linkable / refresh-safe: the order code is in
// the URL.
export function InvoiceDetailClient() {
  const t = useTranslations("billing");
  const { orderCode } = useParams<{ orderCode: string }>();
  const orgId = useAuthStore((s) => s.activeOrgId);
  const { data, isLoading, isError } = usePlanPurchaseDetail(orgId, orderCode);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("detailTitle")}
        actions={
          <Link
            to="/billing"
            className="rounded-full border border-smoke bg-paper px-5 py-2.5 text-sm font-bold text-ink transition hover:bg-mist"
          >
            {t("detailBack")}
          </Link>
        }
      />

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      ) : isError || !data ? (
        <p className="rounded-2xl border border-smoke bg-paper p-6 text-center text-sm text-ash">
          {t("detailNotFound")}
        </p>
      ) : (
        <InvoiceBody purchase={data} orgId={orgId ?? ""} />
      )}
    </div>
  );
}

function InvoiceBody({ purchase, orgId }: { purchase: Detail; orgId: string }) {
  const t = useTranslations("billing");
  const { upgradeTo, openTopup } = useSubscriptionModals();
  const qr = usePlanPurchaseQr(orgId);
  const qc = useQueryClient();
  const [qrData, setQrData] = useState<PlanPurchaseQrResponse | null>(null);

  // When the polling detail flips to PAID, the plan is now active — refresh the
  // plan status + insights caches so the dashboard / postings pages drop the
  // stale "at limit" state without a manual refresh. Fire once on transition.
  const settledRef = useRef(false);
  useEffect(() => {
    if (purchase.status === "PAID" && !settledRef.current && orgId) {
      settledRef.current = true;
      qc.invalidateQueries({ queryKey: qk.orgs.planStatus(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.insights(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.planPurchases(orgId) });
    }
  }, [purchase.status, orgId, qc]);

  const itemLabel =
    purchase.kind === "PLAN"
      ? t("itemPlan", { plan: purchase.planName ?? purchase.planCode ?? "" })
      : purchase.kind === "SOURCING"
        ? t("itemSourcing", { qty: purchase.quantity ?? 0 })
        : t("itemBoost", { qty: purchase.quantity ?? 0 });

  const kindLabel =
    purchase.kind === "PLAN"
      ? t("kindPlan")
      : purchase.kind === "SOURCING"
        ? t("kindSourcing")
        : t("kindBoost");

  // Validity window for a settled PLAN order: paidAt → paidAt + durationDays.
  const validity =
    purchase.kind === "PLAN" &&
    purchase.status === "PAID" &&
    purchase.paidAt &&
    purchase.durationDays != null
      ? {
          from: purchase.paidAt,
          to: new Date(
            new Date(purchase.paidAt).getTime() +
              purchase.durationDays * 24 * 60 * 60 * 1000,
          ).toISOString(),
        }
      : null;

  async function reopenQr() {
    try {
      const data = await qr.mutateAsync(purchase.orderCode);
      setQrData(data);
    } catch {
      // The QR is only regenerable while PENDING; if it failed the order likely
      // just settled — the polling hook will flip the status shortly.
    }
  }

  function recreate() {
    if (purchase.kind === "PLAN" && purchase.planCode) {
      upgradeTo(purchase.planCode);
    } else if (purchase.kind === "SOURCING" || purchase.kind === "BOOST") {
      openTopup(purchase.kind);
    }
  }

  return (
    <>
      {/* Status banner — the "result" affordance. */}
      <StatusBanner status={purchase.status} deadlineAt={purchase.deadlineAt} />

      <Card bordered className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-title-md font-extrabold text-ink">
              {itemLabel}
            </p>
            <p className="mt-0.5 text-body-sm text-ash">{kindLabel}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="font-mono text-title-md font-bold text-ink">
              {formatVnd(purchase.amountVnd)}
            </span>
            <Badge tone={STATUS_TONE[purchase.status]}>
              {t(`status${purchase.status}`)}
            </Badge>
          </div>
        </div>

        <dl className="mt-4 space-y-0.5 border-t border-mist pt-4 text-body-sm">
          <Row label={t("detailOrderCode")} value={purchase.orderCode} mono />
          <Row
            label={t("detailCreatedAt")}
            value={formatDateTimeVn(purchase.createdAt)}
          />
          {purchase.paidAt ? (
            <Row
              label={t("detailPaidAt")}
              value={formatDateTimeVn(purchase.paidAt)}
            />
          ) : null}
          {validity ? (
            <Row
              label={t("detailValidity")}
              value={`${formatDateTimeVn(validity.from)} → ${formatDateTimeVn(
                validity.to,
              )}`}
            />
          ) : null}
        </dl>

        {/* Receiving account + transfer memo — for reconciliation, any status. */}
        <div className="mt-4 rounded-xl bg-mist p-3 text-body-sm text-ink">
          <Row label={t("detailAccountName")} value={purchase.accountName} />
          <Row label={t("detailAccountNo")} value={purchase.accountNo} mono />
          <Row label={t("detailAddInfo")} value={purchase.addInfo} mono />
        </div>

        {purchase.status === "PENDING" ? (
          <Button
            className="mt-4 w-full"
            loading={qr.isPending}
            onClick={reopenQr}
          >
            {t("detailReopenQr")}
          </Button>
        ) : null}

        {purchase.status === "FAILED" || purchase.status === "EXPIRED" ? (
          <Button className="mt-4 w-full" onClick={recreate}>
            {t("detailRecreate")}
          </Button>
        ) : null}
      </Card>

      <VietQrDialog
        qr={qrData}
        open={!!qrData}
        orgId={orgId}
        onClose={() => setQrData(null)}
      />
    </>
  );
}

function StatusBanner({
  status,
  deadlineAt,
}: {
  status: Detail["status"];
  deadlineAt: string;
}) {
  const t = useTranslations("billing");
  if (status === "PENDING") {
    return (
      <div className="rounded-2xl border border-sun/40 bg-sun/10 p-4">
        <p className="text-title-md font-bold text-ink">
          {t("bannerPendingTitle")}
        </p>
        <p className="mt-1 text-body-sm text-ash">{t("bannerPendingBody")}</p>
        <p className="mt-1 text-body-xs text-ash">
          {t("bannerPendingDeadline", {
            deadline: formatDateTimeVn(deadlineAt),
          })}
        </p>
      </div>
    );
  }
  if (status === "PAID") {
    return (
      <div className="rounded-2xl border border-lime/40 bg-lime/10 p-4">
        <p className="text-title-md font-bold text-ink">
          {t("bannerPaidTitle")}
        </p>
        <p className="mt-1 text-body-sm text-ash">{t("bannerPaidBody")}</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-ember/40 bg-ember/10 p-4">
      <p className="text-title-md font-bold text-ink">
        {t(status === "EXPIRED" ? "bannerExpiredTitle" : "bannerFailedTitle")}
      </p>
      <p className="mt-1 text-body-sm text-ash">
        {t(status === "EXPIRED" ? "bannerExpiredBody" : "bannerFailedBody")}
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-0.5">
      <dt className="shrink-0 text-ash">{label}</dt>
      <dd
        className={`min-w-0 break-all text-right font-semibold text-ink${
          mono ? " font-mono" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
